import React, { useMemo, useContext, useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useStyles } from "./TopFiveFailureCodes.styles";
import { HMIContext } from "../../../contexts/HMIContext/HMIContext";
import { BarsChartFailureCodes } from "../../../components/BarsChartFailureCodes/BarsChartFailureCodes.view";
import { NoStatsData } from "../HMIDashboard/NoStatsData/NoStatsData.view";
import {
    FilterFailureTypesByFailureCode,
    IChartBar,
    IFailureCodesByFailureTypes,
    IGraphProps,
} from "./TopFiveFailureCodesInterfaces";
import { GraphsNotEnoughDataInMinutes } from "components/GraphsNotEnoughDataInMinutes/GraphsNotEnoughDataInMinutes.view";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";

export const TopFiveFailureCodes = ({ downtimeEvents, showTitle, liveDowntimeReport, countryCode }: IGraphProps) => {
    const { t, i18n } = useTranslation();
    const classes = useStyles();
    const { displayUnit } = useContext(HMIContext);
    const [noInitialDataInMinutes, setNoInitialDataInMinutes] = useState<boolean>(false);

    //we're mapping the data that comes from the backend and grouping it by failure type
    const failureCodesByFailureTypeName: { [key: string]: FilterFailureTypesByFailureCode[] } | {} = useMemo(
        () =>
            downtimeEvents
                ? downtimeEvents
                      .filter((event) => event.failure_code && event.failure_code !== undefined)
                      .map((event) => {
                          const defaultFailureTypeTranslatedName = translateDefaultEntity(
                              i18n.language,
                              event.failure_type,
                              "name",
                              DefaultTranslationFieldsInTable.FAILURE_TYPE,
                          );

                          const defaultFailureCodeTranslatedName = translateDefaultEntity(
                              i18n.language,
                              event.failure_code,
                              "name",
                              DefaultTranslationFieldsInTable.FAILURE_CODE,
                          );
                          return {
                              failure_type_id: event.failure_type.failure_type_id,
                              failure_type_name: defaultFailureTypeTranslatedName,
                              machine_center_id: event.machine_center_id,
                              color_code: event.failure_type.color_code,
                              failure_code_id: event.failure_code.failure_code_id,
                              failure_code_name: defaultFailureCodeTranslatedName,
                              total_minutes: event.validTime,
                              total_occurrences: 1,
                          };
                      })
                      .reduce((acc: { [key: string]: FilterFailureTypesByFailureCode[] }, obj) => {
                          (acc[obj.failure_type_name] = acc[obj.failure_type_name] || []).push(obj);
                          return acc;
                      }, {})
                : {},
        [downtimeEvents, i18n.language],
    );

    //we're adding up total_occurrences and total_minutes for each repeated failure code inside each failure type
    const groupedDataByFailureCode: { [key: string]: FilterFailureTypesByFailureCode[] } = useMemo(() => {
        if (!failureCodesByFailureTypeName) return {};

        const result: { [key: string]: FilterFailureTypesByFailureCode[] } = {};

        Object.keys(failureCodesByFailureTypeName).forEach((key: string) => {
            const failureCodes = (
                failureCodesByFailureTypeName as { [key: string]: FilterFailureTypesByFailureCode[] }
            )[key];

            const groupedFailureCodes: { [key: number]: FilterFailureTypesByFailureCode } = {};

            failureCodes.forEach((failureCode: FilterFailureTypesByFailureCode) => {
                const { failure_code_id, total_minutes, total_occurrences } = failureCode;

                if (groupedFailureCodes[failure_code_id]) {
                    groupedFailureCodes[failure_code_id].total_minutes += total_minutes;
                    groupedFailureCodes[failure_code_id].total_occurrences += total_occurrences;
                } else {
                    groupedFailureCodes[failure_code_id] = { ...failureCode };
                }
            });

            displayUnit === "minutes" &&
                Object.values(groupedFailureCodes).forEach(
                    (item) => (item.total_minutes = item.total_minutes < 60 ? 1 : Math.round(item.total_minutes / 60)),
                );

            result[key] = Object.values(groupedFailureCodes);
        });

        return result;
    }, [failureCodesByFailureTypeName, displayUnit]);

    //we're sorting it by the top five failure codes in occurrences or minutes, in descending order
    const failureCodesByMinutesOrOccurrences = useMemo(() => {
        const data =
            displayUnit === "minutes"
                ? Object.values(groupedDataByFailureCode).map((failureCodes) =>
                      Object.values(failureCodes)
                          .sort((a, b) => b.total_minutes - a.total_minutes)
                          .slice(0, 5)
                          .reverse()
                          .reduce(
                              (
                                  acc: { [key: string]: FilterFailureTypesByFailureCode[] },
                                  obj: FilterFailureTypesByFailureCode,
                              ) => {
                                  (acc[obj.failure_type_name] = acc[obj.failure_type_name] || []).push(obj);
                                  return acc;
                              },
                              {},
                          ),
                  )
                : Object.values(groupedDataByFailureCode).map((failureCode) =>
                      Object.values(failureCode)
                          .sort((a, b) => b.total_occurrences - a.total_occurrences)
                          .slice(0, 5)
                          .reverse()
                          .reduce(
                              (
                                  acc: { [key: string]: FilterFailureTypesByFailureCode[] },
                                  obj: FilterFailureTypesByFailureCode,
                              ) => {
                                  (acc[obj.failure_type_name] = acc[obj.failure_type_name] || []).push(obj);
                                  return acc;
                              },
                              {},
                          ),
                  );

        return data;
    }, [displayUnit, groupedDataByFailureCode]);

    //we're mapping the data as Nivo needs it for its graphs
    const graphData: IFailureCodesByFailureTypes[] = useMemo(() => {
        const allFailureCodesByFailureTypesArray: IFailureCodesByFailureTypes[] = [];
        failureCodesByMinutesOrOccurrences.forEach(
            (failureCode: { [key: string]: FilterFailureTypesByFailureCode[] }) => {
                for (const [key, value] of Object.entries(failureCode)) {
                    const failureCodesByFailureType: IChartBar[] = [];
                    Object.values(value).forEach((valueObject) => {
                        const failureCodeObject: IChartBar = {};
                        failureCodeObject["countryCode"] = countryCode!;
                        if ((displayUnit === "minutes" && valueObject.total_minutes) || displayUnit === "occurrences") {
                            failureCodeObject["failureCodeName"] = valueObject.failure_code_name;
                            failureCodeObject[`${valueObject.failure_code_name}Color`] = valueObject.color_code;
                            failureCodeObject[`${valueObject.failure_code_name}`] =
                                displayUnit === "minutes" ? valueObject.total_minutes : valueObject.total_occurrences;
                            failureCodesByFailureType.push(failureCodeObject);
                        }
                    });
                    allFailureCodesByFailureTypesArray.push({
                        failureType: key,
                        failureCodes: failureCodesByFailureType,
                    });
                }
            },
        );

        return allFailureCodesByFailureTypesArray;
    }, [displayUnit, failureCodesByMinutesOrOccurrences, countryCode]);

    //if we have no initial data in minutes yet, we're setting the correct variable to display the matching screen
    useEffect(() => {
        if (graphData) {
            setNoInitialDataInMinutes(graphData.every((item) => item.failureCodes.length === 0));
        }
    }, [graphData]);

    //we're sorting the data by most frequent or most minute-accumulating failure type
    const sortedFailureTypes = useMemo(() => {
        const sortedData = graphData.sort((a: IFailureCodesByFailureTypes, b: IFailureCodesByFailureTypes) => {
            const sumA = Object.values(a.failureCodes).reduce((acc: number, current: IChartBar) => {
                const numericValues = Object.values(current).filter((val) => typeof val === "number") as number[];
                return acc + numericValues.reduce((sum, val) => sum + val, 0);
            }, 0);
            const sumB = Object.values(b.failureCodes).reduce((acc: number, current: IChartBar) => {
                const numericValues = Object.values(current).filter((val) => typeof val === "number") as number[];
                return acc + numericValues.reduce((sum, val) => sum + val, 0);
            }, 0);
            return sumB - sumA;
        });

        return sortedData;
    }, [graphData]);

    return (
        <Box className={classes.container}>
            {showTitle && (
                <Typography variant="h1" className={classes.TopFiveFailureCodesTitle}>
                    {t("HMI.top_five_failure_codes.title")}
                </Typography>
            )}

            <>
                {downtimeEvents && downtimeEvents.length > 0 ? (
                    noInitialDataInMinutes ? (
                        <GraphsNotEnoughDataInMinutes />
                    ) : (
                        <Box className={classes.graphContainer} sx={{ width: liveDowntimeReport ? "70vw" : "85vw" }}>
                            {Object.values(sortedFailureTypes).map((failureTypeAndCodes, index) => {
                                if (
                                    failureTypeAndCodes.failureCodes[0] &&
                                    Object.values(failureTypeAndCodes.failureCodes[0]).some(
                                        (item: string | number | null) =>
                                            typeof item === "number" && !isNaN(item) && item > 0,
                                    )
                                ) {
                                    return (
                                        <Box
                                            key={`${index}_${Math.random()}`}
                                            className={classes.graphGridItemContainer}
                                        >
                                            <Typography className={classes.graphTitle} variant="subtitle1">
                                                {failureTypeAndCodes.failureType}
                                            </Typography>
                                            <BarsChartFailureCodes data={failureTypeAndCodes.failureCodes} />
                                        </Box>
                                    );
                                } else {
                                    return null;
                                }
                            })}
                        </Box>
                    )
                ) : (
                    <NoStatsData />
                )}
            </>
        </Box>
    );
};
